import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ActivityFilters = ({ filters, onFilterChange, onClearFilters, activityTypes }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [isExpanded, setIsExpanded] = useState(true);

  // Update local state when props change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleTypeToggle = (typeValue) => {
    const updatedTypes = localFilters.types.includes(typeValue)
      ? localFilters.types.filter(type => type !== typeValue)
      : [...localFilters.types, typeValue];
    
    const updatedFilters = { ...localFilters, types: updatedTypes };
    setLocalFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  const handleDateChange = (field, value) => {
    const updatedFilters = { ...localFilters, [field]: value };
    setLocalFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  const handleSearchChange = (e) => {
    const updatedFilters = { ...localFilters, search: e.target.value };
    setLocalFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  const handleClearFilters = () => {
    onClearFilters();
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center">
          <Icon name="Filter" size={18} className="text-primary mr-2" />
          <h3 className="font-semibold text-text-primary">Filter Activities</h3>
        </div>
        <button className="text-text-secondary hover:text-primary transition-colors">
          <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={18} />
        </button>
      </div>

      {/* Filter Content */}
      {isExpanded && (
        <div className="p-4 space-y-6">
          {/* Search Filter */}
          <div className="space-y-2">
            <label htmlFor="search-filter" className="block text-sm font-medium text-text-primary">
              Search
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon name="Search" size={16} className="text-gray-400" />
              </div>
              <input
                type="text"
                id="search-filter"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm"
                placeholder="Search activities..."
                value={localFilters.search}
                onChange={handleSearchChange}
              />
            </div>
          </div>

          {/* Activity Type Filter */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-text-primary">
              Activity Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              {activityTypes.map((type) => (
                <div 
                  key={type.value}
                  className={`flex items-center p-2 rounded-md cursor-pointer transition-colors ${
                    localFilters.types.includes(type.value) 
                      ? 'bg-primary-50 border border-primary-200' 
                      : 'bg-white border border-gray-200 hover:bg-gray-50'
                  }`}
                  onClick={() => handleTypeToggle(type.value)}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 ${
                    localFilters.types.includes(type.value) 
                      ? 'bg-primary-100 text-primary' 
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    <Icon name={type.icon} size={12} />
                  </div>
                  <span className={`text-xs ${
                    localFilters.types.includes(type.value) 
                      ? 'text-primary font-medium' 
                      : 'text-text-secondary'
                  }`}>
                    {type.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-text-primary">
              Date Range
            </label>
            <div className="space-y-2">
              <div>
                <label htmlFor="start-date" className="block text-xs text-text-secondary mb-1">
                  From
                </label>
                <input
                  type="date"
                  id="start-date"
                  className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm"
                  value={localFilters.startDate || ''}
                  onChange={(e) => handleDateChange('startDate', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="end-date" className="block text-xs text-text-secondary mb-1">
                  To
                </label>
                <input
                  type="date"
                  id="end-date"
                  className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm"
                  value={localFilters.endDate || ''}
                  onChange={(e) => handleDateChange('endDate', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Filter Actions */}
          <div className="pt-2 flex flex-col space-y-2">
            <Button 
              variant="outline" 
              iconName="RefreshCw" 
              className="w-full"
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
            
            {/* Filter Summary */}
            {(localFilters.types.length > 0 || localFilters.startDate || localFilters.endDate || localFilters.search) && (
              <div className="bg-gray-50 p-3 rounded-md mt-2">
                <div className="text-xs text-text-secondary">
                  <strong>Active Filters:</strong>
                  <ul className="mt-1 space-y-1">
                    {localFilters.types.length > 0 && (
                      <li>• {localFilters.types.length} activity types</li>
                    )}
                    {localFilters.startDate && (
                      <li>• From: {new Date(localFilters.startDate).toLocaleDateString()}</li>
                    )}
                    {localFilters.endDate && (
                      <li>• To: {new Date(localFilters.endDate).toLocaleDateString()}</li>
                    )}
                    {localFilters.search && (
                      <li>• Search: "{localFilters.search}"</li>
                    )}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityFilters;
